// frontend/src/pages/public/HomePage.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  BoltIcon,
  ShieldCheckIcon,
  CodeBracketIcon,
  ChartBarIcon,
  ArrowPathIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import Seo from "../../marketing/Seo";
import HeroGraphic from "../../marketing/HeroGraphic";
import { Container, Section, Eyebrow, Badge, Button } from "../../marketing/ui";
import { FeatureGrid, LogoCloud, StatStrip, CTASection } from "../../marketing/blocks";

const PILLARS = [
  {
    icon: BoltIcon,
    title: "Honest pricing",
    description:
      "Real code counts on every tier and no per-scan fees. Your redirects never stop working — we just nudge you when it's time to grow.",
  },
  {
    icon: ShieldCheckIcon,
    title: "Privacy-first analytics",
    description:
      "GDPR by design. QRFlow never stores raw IP addresses — you get the insight without the liability.",
  },
  {
    icon: CodeBracketIcon,
    title: "Built for builders",
    description:
      "A clean REST API, scoped API keys, webhooks and A/B testing. Automate codes and pull analytics straight into your stack.",
  },
  {
    icon: ChartBarIcon,
    title: "Real-time insight",
    description:
      "Watch scans land live, break them down by device and UTM, and see where your audience is with location heatmaps.",
  },
  {
    icon: ArrowPathIcon,
    title: "Truly dynamic",
    description:
      "Change a code's destination any time without reprinting — on every plan, including Free.",
  },
  {
    icon: TagIcon,
    title: "Organised at scale",
    description:
      "Folders, tags and bulk actions keep thousands of codes tidy across your whole team.",
  },
];

const STEPS = [
  { n: "1", title: "Create a dynamic code", body: "Pick a type, paste your destination, name it. QRFlow mints a stable short URL." },
  { n: "2", title: "Place it anywhere", body: "Download crisp PNG or print-ready SVG and put it on packaging, flyers or screens." },
  { n: "3", title: "Track every scan", body: "See scans in real time, edit the destination later, and A/B test what converts." },
];

export default function HomePage() {
  return (
    <>
      <Seo
        title="Dynamic QR codes with real-time, privacy-first analytics"
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "QRFlow",
          url: typeof window !== "undefined" ? window.location.origin : "",
        }}
      />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-b from-brand-50/60 to-white">
        <Container className="grid items-center gap-12 py-16 sm:py-24 lg:grid-cols-2">
          <div className="animate-fade-up">
            <Badge tone="accent">No per-scan fees · Free plan, no card</Badge>
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              QR codes that stay{" "}
              <span className="text-brand-600">dynamic</span> — and analytics you can trust
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-gray-600">
              Create dynamic QR codes, change their destination any time, and watch scans
              land in real time. Honest pricing, a developer-friendly API, and GDPR-first
              analytics that never store raw IPs.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button to="/register" size="lg">Get started free</Button>
              <Button to="/pricing" size="lg" variant="secondary">See pricing</Button>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Free forever for 5 codes · upgrade only when you grow.
            </p>
          </div>
          <div className="animate-fade-up">
            <HeroGraphic className="mx-auto w-full max-w-lg drop-shadow-sm" />
          </div>
        </Container>
      </div>

      <LogoCloud logos={["Northwind", "Acme Co", "Lumen", "Brightline", "Vela", "Quanta"]} />

      <FeatureGrid
        eyebrow="Why QRFlow"
        title="Everything you need, nothing you don't"
        subtitle="A focused toolkit that does dynamic QR and analytics exceptionally well."
        features={PILLARS}
      />

      <StatStrip
        stats={[
          { label: "Plans, clearly priced", value: "4" },
          { label: "Dynamic redirects", value: "∞" },
          { label: "Raw IPs stored", value: "0" },
          { label: "Free codes to start", value: "5" },
        ]}
      />

      {/* How it works */}
      <Section>
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            From idea to tracked scans in minutes
          </h2>
        </div>
        <div className="mt-14 grid gap-8 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-2xl bg-gray-50 p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white">
                {s.n}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/docs/getting-started" className="text-sm font-semibold text-brand-700 hover:text-brand-800">
            Read the getting-started guide →
          </Link>
        </div>
      </Section>

      <CTASection
        title="Start free. Grow when you're ready."
        subtitle="No credit card. Five dynamic codes, real-time analytics, and the full toolkit a click away."
        primary={{ to: "/register", label: "Get started free" }}
        secondary={{ to: "/pricing", label: "Compare plans" }}
      />
    </>
  );
}
